import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import AdminConfirmDialog from "./AdminConfirmDialog";
import { Plus, Trash2, ScrollText } from "lucide-react";

export default function SettingsTab() {
    const [settings, setSettings] = useState(null);
    const [audit, setAudit] = useState({ items: [], total: 0, actions: [] });
    const [actionFilter, setActionFilter] = useState("all");
    const [auditPage, setAuditPage] = useState(1);
    const auditPageSize = 25;
    const [pending, setPending] = useState(null); // { kind, payload }

    const loadSettings = async () => {
        try {
            const { data } = await api.get("/admin/settings");
            setSettings(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const loadAudit = async () => {
        try {
            const { data } = await api.get("/admin/audit-log", {
                params: { action: actionFilter, page: auditPage, page_size: auditPageSize },
            });
            setAudit(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    useEffect(() => { loadSettings(); }, []);
    useEffect(() => { loadAudit(); /* eslint-disable-next-line */ }, [actionFilter, auditPage]);
    useEffect(() => { setAuditPage(1); }, [actionFilter]);

    const onConfirm = async (reason) => {
        if (!pending) return;
        try {
            await api.put(`/admin/settings/${pending.kind}`, { value: pending.payload, reason });
            toast.success("Saved");
            setPending(null);
            await loadSettings();
            await loadAudit();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (!settings) {
        return (
            <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="h-24 bg-snowcap border border-ridge-line rounded-lg animate-pulse" />
                ))}
            </div>
        );
    }

    const totalAuditPages = Math.max(1, Math.ceil(audit.total / auditPageSize));

    return (
        <div className="space-y-6">
            <FeatureFlags
                flags={settings.feature_flags}
                onSave={(flags) => setPending({ kind: "feature_flags", payload: flags })}
            />
            <ReportReasons
                reasons={settings.report_reasons}
                onSave={(reasons) => setPending({ kind: "report_reasons", payload: reasons })}
            />
            <section className="bg-snowcap border border-ridge-line rounded-lg p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h2 className="font-display font-bold text-xl text-midnight-pine flex items-center gap-2">
                        <ScrollText className="w-5 h-5" /> Audit log
                    </h2>
                    <select
                        data-testid="audit-action-filter"
                        value={actionFilter}
                        onChange={(e) => setActionFilter(e.target.value)}
                        className="px-3 py-1.5 rounded-md bg-alpine-mist border border-ridge-line text-xs font-semibold focus:border-ember-orange focus:outline-none"
                    >
                        <option value="all">All actions</option>
                        {audit.actions.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>

                {audit.items.length === 0 ? (
                    <p className="text-slate-gray text-sm">
                        No audit entries{actionFilter !== "all" ? ` for "${actionFilter}"` : ""} yet. Admin actions will appear here.
                    </p>
                ) : (
                    <ul className="divide-y divide-ridge-line">
                        {audit.items.map((row) => (
                            <li
                                key={row.id}
                                data-testid={`audit-row-${row.id}`}
                                className="py-3 grid grid-cols-1 md:grid-cols-[auto,1fr,auto] gap-2 md:gap-4 items-start"
                            >
                                <span className="text-xs font-bold uppercase tracking-widest text-ember-orange whitespace-nowrap">
                                    {row.action}
                                </span>
                                <div className="text-sm">
                                    <span className="font-semibold text-granite">{row.admin_email || row.admin_id}</span>
                                    {row.target_label && (
                                        <>
                                            <span className="text-slate-gray"> · </span>
                                            <span className="text-granite">{row.target_label}</span>
                                        </>
                                    )}
                                    {row.reason && (
                                        <div className="text-xs text-slate-gray italic mt-0.5">"{row.reason}"</div>
                                    )}
                                </div>
                                <span className="text-xs text-slate-gray whitespace-nowrap md:text-right">
                                    {new Date(row.created_at).toLocaleString()}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {audit.total > auditPageSize && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-ridge-line">
                        <span className="text-xs text-slate-gray font-semibold">
                            Page {auditPage} of {totalAuditPages}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="audit-prev"
                                disabled={auditPage <= 1}
                                onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                                className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                            >
                                Prev
                            </button>
                            <button
                                data-testid="audit-next"
                                disabled={auditPage >= totalAuditPages}
                                onClick={() => setAuditPage((p) => Math.min(totalAuditPages, p + 1))}
                                className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </section>

            <AdminConfirmDialog
                open={!!pending}
                onClose={() => setPending(null)}
                onConfirm={onConfirm}
                title="Save settings change?"
                description="Logged to the audit trail."
                confirmLabel="Save"
            />
        </div>
    );
}

function FeatureFlags({ flags, onSave }) {
    const [local, setLocal] = useState(flags);

    React.useEffect(() => setLocal(flags), [flags]);

    const dirty = JSON.stringify(local) !== JSON.stringify(flags);

    const flagLabels = {
        marketplace_enabled: "Marketplace enabled",
        messaging_enabled: "Messaging enabled",
        ads_enabled: "Ads enabled",
        registration_open: "Registration open",
        maintenance_mode: "Maintenance mode",
    };

    return (
        <section className="bg-snowcap border border-ridge-line rounded-lg p-6">
            <h2 className="font-display font-bold text-xl text-midnight-pine mb-4">Feature flags</h2>
            <div className="space-y-2">
                {Object.entries(local).map(([k, v]) => (
                    <label
                        key={k}
                        data-testid={`flag-${k}`}
                        className="flex items-center justify-between py-2 cursor-pointer hover:bg-alpine-mist/50 rounded-md px-2"
                    >
                        <div>
                            <div className="text-sm font-semibold text-granite">{flagLabels[k] || k}</div>
                            <div className="text-xs text-slate-gray">{k}</div>
                        </div>
                        <input
                            type="checkbox"
                            checked={!!v}
                            onChange={(e) => setLocal((f) => ({ ...f, [k]: e.target.checked }))}
                            className="w-5 h-5 accent-ember-orange"
                        />
                    </label>
                ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-ridge-line">
                <button
                    onClick={() => setLocal(flags)}
                    disabled={!dirty}
                    className="px-3 py-1.5 rounded-md text-xs font-bold text-granite hover:bg-alpine-mist disabled:opacity-40"
                >
                    Reset
                </button>
                <button
                    data-testid="flags-save"
                    onClick={() => onSave(local)}
                    disabled={!dirty}
                    className="px-4 py-2 rounded-md text-xs font-bold bg-midnight-pine text-white hover:bg-deep-evergreen disabled:opacity-40"
                >
                    Save flags
                </button>
            </div>
        </section>
    );
}

function ReportReasons({ reasons, onSave }) {
    const [local, setLocal] = useState(reasons);
    const [draft, setDraft] = useState("");

    React.useEffect(() => setLocal(reasons), [reasons]);

    const dirty = JSON.stringify(local) !== JSON.stringify(reasons);

    const add = () => {
        const v = draft.trim();
        if (!v) return;
        if (local.includes(v)) { toast.error("Already in list"); return; }
        setLocal((r) => [...r, v]);
        setDraft("");
    };

    return (
        <section className="bg-snowcap border border-ridge-line rounded-lg p-6">
            <h2 className="font-display font-bold text-xl text-midnight-pine mb-4">Report reasons</h2>
            <ul className="space-y-2 mb-3">
                {local.map((r, i) => (
                    <li
                        key={`${r}-${i}`}
                        data-testid={`reason-${i}`}
                        className="flex items-center justify-between py-1.5 px-3 bg-alpine-mist rounded-md"
                    >
                        <span className="text-sm font-semibold text-granite">{r}</span>
                        <button
                            data-testid={`reason-remove-${i}`}
                            onClick={() => setLocal((rs) => rs.filter((_, idx) => idx !== i))}
                            className="text-slate-gray hover:text-ember-dark"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </li>
                ))}
            </ul>
            <div className="flex gap-2 mb-3">
                <input
                    data-testid="reason-draft"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder="Add a new report reason…"
                    className="flex-1 px-3 py-2 rounded-md bg-alpine-mist border border-ridge-line text-sm focus:border-ember-orange focus:outline-none"
                />
                <button
                    data-testid="reason-add"
                    onClick={add}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-midnight-pine text-white text-xs font-bold hover:bg-deep-evergreen"
                >
                    <Plus className="w-3 h-3" /> Add
                </button>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-ridge-line">
                <button
                    onClick={() => setLocal(reasons)}
                    disabled={!dirty}
                    className="px-3 py-1.5 rounded-md text-xs font-bold text-granite hover:bg-alpine-mist disabled:opacity-40"
                >
                    Reset
                </button>
                <button
                    data-testid="reasons-save"
                    onClick={() => onSave(local)}
                    disabled={!dirty}
                    className="px-4 py-2 rounded-md text-xs font-bold bg-midnight-pine text-white hover:bg-deep-evergreen disabled:opacity-40"
                >
                    Save reasons
                </button>
            </div>
        </section>
    );
}
